// src/axiosConfig.js
import axios from "axios";
import { refreshAuthToken } from "./utils/api_helper_custom";
import { API_BASE_URL } from './config';

// Configure the base instance
const instance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

const skipInterceptorEndpoints = ["/login", "/register", "/refresh-token"];

// Interceptor to add the access token to headers
instance.interceptors.request.use(
    (config) => {
        // Try new auth system first (localStorage), fallback to old (sessionStorage)
        const token = localStorage.getItem('access_token') || sessionStorage.getItem('authToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor for token refresh
instance.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        // Check if this is a refresh token request that failed
        if (error.request?.responseURL?.includes("/refresh-token") && error.response?.status === 401) {
            console.error("Token refresh request failed.");
            // Clear both old and new auth systems
            sessionStorage.removeItem("authToken");
            sessionStorage.removeItem("refreshToken");
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            localStorage.removeItem("auth_method");
            localStorage.removeItem("user");
            window.location.href = "/login";
            return Promise.reject(error);
        }
        
        // Handle 401 errors for other requests
        if (error.response?.status === 401) {
            console.log("Unauthorized request. Attempting token refresh...");

            try {
                const newToken = await refreshAuthToken();

                if (newToken) {
                    console.log("Token refreshed successfully. Retrying request...");
                    originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
                    return axios(originalRequest);
                } else {
                    console.error("Token refresh request failed.");
                    // Clear both old and new auth systems
                    sessionStorage.removeItem("authToken");
                    sessionStorage.removeItem("refreshToken");
                    localStorage.removeItem("access_token");
                    localStorage.removeItem("refresh_token");
                    localStorage.removeItem("auth_method");
                    localStorage.removeItem("user");
                    window.location.href = "/login";
                    return Promise.reject(error);
                }
            } catch (refreshError) {
                console.error("Token refresh failed. Redirecting to login.", refreshError);
                // Clear both old and new auth systems
                sessionStorage.removeItem("authToken");
                sessionStorage.removeItem("refreshToken");
                localStorage.removeItem("access_token");
                localStorage.removeItem("refresh_token");
                localStorage.removeItem("auth_method");
                localStorage.removeItem("user");
                window.location.href = "/login";
                return Promise.reject(refreshError);
            }
        }

        // Handle requests without proper config
        if (!error.config) {
            console.error("Axios encountered an error before request was sent:", error);
            return Promise.reject(error);
        }

        // Skip interceptor for certain endpoints
        const skipInterceptor = skipInterceptorEndpoints.some(endpoint =>
            originalRequest.url.includes(endpoint)
        );

        if (skipInterceptor) {
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);

export default instance;
